import type { CustomTranslationStatusId, ExportConfig, ExportData, ProjectId, Webhook } from '../types';
import getExportFormatValue from './getExportFormatValue';

const prepareWebhooks = (webhooks: readonly Webhook[]): Record<string, string> =>
    webhooks.reduce(
        (accumulator, { url, active }, index) => ({
            ...accumulator,
            [`webhook-${index}`]: url,
            [`webhookactive-${index}`]: active,
        }),
        {},
    );

const prepareExportLangs = (exportLangs: readonly string[]): Record<string, string> =>
    exportLangs.reduce(
        (accumulator, language) => ({
            ...accumulator,
            [`export-lang-${language}`]: true,
        }),
        {},
    );

const prepareExportPids = (exportPids: readonly ProjectId[]): Record<string, string> =>
    exportPids.reduce(
        (accumulator, projectId) => ({
            ...accumulator,
            [`export-pid-${projectId}`]: true,
        }),
        {},
    );

const prepareCustomStatusIds = (statusIds: readonly CustomTranslationStatusId[]): CustomTranslationStatusId => {
    if (statusIds.length) {
        return statusIds.join(',');
    }

    return '';
};

export const prepareFilterFileNames = (filterFilenames: readonly string[]): string => {
    if (filterFilenames.length) {
        return filterFilenames.map((filename) => filename.replace(/,/g, '%COMMA%')).join(',');
    }

    return '';
};

const prepareExportData = ({
    values,
    projectBranchName,
    isPreview = false,
    projectId,
}: {
    values: ExportConfig;
    projectBranchName: null | string;
    isPreview?: boolean;
    projectId: string;
}): ExportData => ({
    action: 'download_files',
    project_id: projectId,
    debug: isPreview ? 1 : 0,
    branch: projectBranchName,
    config: {
        ...prepareWebhooks(values.webhooks),
        ...prepareExportLangs(values.exportLangs),
        ...prepareExportPids(values.exportPids),
        customIsoCodes: values.customIsoCodes ?? {},
        replaceLineBreaks: values.replaceLineBreaks,
        export_format: getExportFormatValue(values.fileFormat, values),
        file_format: values.fileFormat,
        switch_include_all: values.switchIncludeAll,
        'filenames-custom': values.filenamesCustom,
        zip_structure: values.zipStructure,
        'filenames-original': values.filenamesOriginal,
        directory_prefix: values.directoryPrefix,
        java_properties_encoding: values.javaPropertiesEncoding,
        include_tags: values.includeTags,
        exclude_tags: values.excludeTags,
        filter_filenames: prepareFilterFileNames(values.filterFilenames),
        export_empty: values.exportEmpty,
        export_null_as: values.exportNullAs,
        export_sort: values.exportSort,
        java_properties_separator: values.javaPropertiesSeparator,
        plural_format: values.pluralFormat,
        switch_icu_numeric: values.switchIcuNumeric,
        placeholder_format: values.placeholderFormat,
        switch_escape_percent: values.switchEscapePercent,
        indentation: { [values.fileFormat]: values.indentation },
        switch_webhooks: values.switchWebhooks,
        switch_compact: values.switchCompact,
        switch_include_description: values.switchIncludeDescription,
        switch_include_comments: values.switchIncludeComments,
        switch_no_language_folders: values.switchNoLanguageFolders,
        switch_disable_references: values.switchDisableReferences,
        switch_yaml_include_root: values.switchYamlIncludeRoot,
        json_unescaped_slashes: values.jsonUnescapedSlashes,
        add_newline_eof: values.addNewlineEof,
        export_all: values.exportAll,
        export_guntranslated: values.exportGuntranslated,
        export_gtranslated: values.exportGtranslated,
        export_gproofread: values.exportGproofread,
        export_glast_reviewed: values.exportGlastReviewed,
        export_gnonfuzzy: values.exportGnonfuzzy,
        export_gnonhidden: values.exportGnonhidden,
        triggers_amazons3: values.triggersAmazons3,
        triggers_gcs: values.triggersGcs,
        triggers_github: values.triggersGithub,
        triggers_github_enterprise: values.triggersGithubEnterprise,
        triggers_gitlab: values.triggersGitlab,
        triggers_bitbucket: values.triggersBitbucket,
        triggers_bitbucket_enterprise: values.triggersBitbucketEnterprise,
        triggers_azure: values.triggersAzure,
        default_reviewers_bitbucket: values.defaultBitbucketReviewers ?? false,
        filter_repositories: values.filterRepositories,
        commit_template: values.commitTemplate,
        custom_translation_status_ids: prepareCustomStatusIds(values.customTranslationStatusIds),
    },
});

export default prepareExportData;
